/**
 * Role Testing Floating Action Button (FAB)
 *
 * Appears when admin role testing is active. Provides quick access to switch roles.
 * Uses standard MUI components and theme colors.
 */
import BadgeIcon from "@mui/icons-material/Badge";
import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  Fab,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useActiveFBOs } from "../containers/ActiveFBOContainer";
import { useRoleOverride, OverridableRole } from "../stores/useRoleOverride";
import { ROLE_OPTIONS, formatRoleName } from "../utils/permissions";

export const RoleTestingFab: React.FC = () => {
  const { activeFBO } = useActiveFBOs();
  const { isActive, overriddenRole, startRoleOverride, stopRoleOverride } = useRoleOverride();
  const [expanded, setExpanded] = React.useState(false);
  const [selectedRole, setSelectedRole] = React.useState<OverridableRole>(overriddenRole);

  // Sync selectedRole with store
  React.useEffect(() => {
    if (overriddenRole) {
      setSelectedRole(overriddenRole);
    }
  }, [overriddenRole]);

  // Don't render if override is not active
  if (!isActive) {
    return null;
  }

  const handleRoleChange = (newRole: OverridableRole) => {
    setSelectedRole(newRole);
    if (activeFBO?.id) {
      startRoleOverride(newRole, activeFBO.id);
    }
  };

  const handleExit = () => {
    stopRoleOverride();
    setExpanded(false);
  };

  return (
    <>
      {expanded && (
        <Paper
          elevation={8}
          sx={{
            position: "fixed",
            bottom: (theme) => theme.spacing(10),
            right: (theme) => theme.spacing(2),
            p: 2,
            zIndex: 1300,
            minWidth: 280,
          }}
        >
          <Stack direction="column" spacing={2}>
            <Typography variant="subtitle1" fontWeight="bold">
              Role Testing
            </Typography>

            <FormControl fullWidth size="small">
              <InputLabel>Testing as</InputLabel>
              <Select
                value={selectedRole}
                onChange={(e) => handleRoleChange(e.target.value as OverridableRole)}
                label="Testing as"
              >
                {ROLE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              color="secondary"
              startIcon={<CloseIcon />}
              onClick={handleExit}
              fullWidth
            >
              Exit Role Testing
            </Button>
          </Stack>
        </Paper>
      )}

      <Tooltip
        title={
          expanded
            ? "Close"
            : `Testing as ${formatRoleName(overriddenRole)} - Click to change`
        }
      >
        <Fab
          color="secondary"
          onClick={() => setExpanded(!expanded)}
          sx={{
            position: "fixed",
            bottom: (theme) => theme.spacing(2),
            right: (theme) => theme.spacing(2),
          }}
        >
          {expanded ? <CloseIcon /> : <BadgeIcon />}
        </Fab>
      </Tooltip>
    </>
  );
};
